<template>
  <section class="my-12">
    <h4>Find your local dealer?</h4>
    <div ref="mapContainer" style="width: 100%; height: 500px; border-radius: 16px; overflow: hidden;" />
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useBlogStore } from '@/stores/blogStore'

const config = useRuntimeConfig() 
const blogStore = useBlogStore()
const mapContainer = ref(null)

// Load Google Maps using @nuxt/scripts
const { load } = useScriptGoogleMaps({
  apiKey: config.public.VITE_GOOGLE_MAPS_API_KEY,
})

const dynamicStores = computed(() => {
  const articles = blogStore.blogsByHandle['store-locator']?.articles || []

  return articles
    .map(article => {
      const match = article.excerpthtml.match(
        /https:\/\/www\.google\.com\/maps\/place\/.*?@(-?\d+\.\d+),(-?\d+\.\d+)/
      )
      if (!match) return null

      const [, lat, lng] = match
      const linkMatch = article.excerpthtml.match(/<a[^>]*href="(https:\/\/www\.google\.com\/maps\/place\/[^"]+)"[^>]*>See map and directions<\/a>/)

      return {
        name: article.title,
        position: {
          lat: parseFloat(lat),
          lng: parseFloat(lng)
        },
        description: `
          <div style="font-size: 14px; line-height: 1.4">
            <strong>${article.title}</strong><br>
            <p>${article.contenthtml}</p>
          </div>
        `
      }
    })
    .filter(Boolean)
})

onMounted(async () => {
  // Load Google Maps script
  await load({ libraries: ['marker'] })
  
  // Wait for Google Maps to be fully available
  if (!window.google?.maps?.marker) {
    console.error('Google Maps failed to load')
    return
  }

  const maps = window.google.maps

  try {
    const bounds = new maps.LatLngBounds()

    const map = new maps.Map(mapContainer.value, {
      center: { lat: 50.5, lng: 10 },
      zoom: 5,
      mapId: config.public.VITE_GOOGLE_MAPS_ID, 
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      cameraControl: false,
    })

    dynamicStores.value.forEach(store => {
      const marker = new maps.marker.AdvancedMarkerElement({
        position: store.position,
        map,
        title: store.name,
      })

      const infoWindow = new maps.InfoWindow({
        content: store.description
      })

      marker.addListener('click', () => {
        // Open info window
        infoWindow.open(map, marker);

        // Smoothly zoom to level 10 and center on the marker
        smoothZoom(map, 10, map.getZoom(), marker.position);
      });

      // Function to smoothly zoom the map and keep it centered on the marker
      function smoothZoom(map, targetZoom, currentZoom, center) {
        if (currentZoom < targetZoom) {
          const zoomStep = currentZoom + 0.1; // Increment zoom level
          map.setZoom(zoomStep);
          map.panTo(center); // Keep the map centered on the marker
          setTimeout(() => smoothZoom(map, targetZoom, zoomStep, center), 25); // Repeat until targetZoom is reached
        } else {
          map.setZoom(targetZoom); // Ensure the final zoom level is set
          map.panTo(center); // Ensure the map is centered on the marker
        }
      }

      bounds.extend(store.position)
    })

    if (dynamicStores.value.length > 1) {
      map.fitBounds(bounds)
      map.addListener('bounds_changed', () => {
        if (map.getZoom() > 10) map.setZoom(10)
      })
    }
  } catch (error) {
    console.error('Error initializing Google Maps:', error)
  }
})
</script>

